<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_mode,
	} from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from '../../../../helpers/utilities';
	//Stores
	import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Alert, Button, Input, Text, toast } from '@Shelly/shelly-design-system';
	//Config
	import { devices_config } from '@devices/index';

	export let device;

	let id = real_id(device.id);

	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	$: status = devices_status.getByRealId(id);

	let colorTempRange = settings[`${get_gen_2_component_channel_structure(device)}`]?.ct_range;
	let cacheRange = colorTempRange;
	let colorTempRangeMin = colorTempRange[0];
	let colorTempRangeMax = colorTempRange[1];

	let loading = false;

	let min = 1000;
	let max = 7000;

	$: {
		let componentType: string | undefined;
		const componentPath = get_gen_2_component_channel_structure(device);
		if (componentPath) {
			componentType = componentPath.split(':')[0];
		}

		const mode = get_mode(device, status, settings);

		let deviceConfig;
		if (componentType === 'cct') {
			deviceConfig = devices_config?.[device.type]?.modes?.['cct']?.color_temp;
		} else if (componentType === 'rgbw') {
			deviceConfig = devices_config?.[device.type]?.modes?.['rgbw']?.color_temp;
		} else {
			deviceConfig = devices_config?.[device.type]?.modes?.[mode]?.color_temp;
		}

		if (deviceConfig?.minValue && deviceConfig?.maxValue) {
			min = deviceConfig.minValue;
			max = deviceConfig.maxValue;
		} else {
			min = 1000;
			max = 7000;
		}
	}

	const setcolorTempRange = async () => {
		if (cacheRange[0] === colorTempRangeMin && cacheRange[1] === colorTempRangeMax) return;
		if (colorTempRangeMin < min || colorTempRangeMax > max) {
			return toast.open({
				type: 'danger',
				title:
					$format('movement_limit_toast', {
						values: { min: min, max: max },
					}) || 'Values must be between min - max!',
			});
		}
		loading = true;

		const params = {
			id: get_device_component_index(device),
			config: {
				ct_range: [+colorTempRangeMin, +colorTempRangeMax],
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response) {
			settings[`${get_gen_2_component_channel_structure(device)}`].ct_ramge = [
				colorTempRangeMin,
				colorTempRangeMax,
			];
			devices_settings.update_devices_settings(id, settings);
			cacheRange = [colorTempRangeMin, colorTempRangeMax];
		} else {
			colorTempRangeMin = cacheRange[0];
			colorTempRangeMax = cacheRange[1];
		}
		loading = false;
	};
	const validate = () => {
		if (+colorTempRangeMax <= +colorTempRangeMin) {
			colorTempRangeMax = +colorTempRangeMin + 1;
		}
		if (+colorTempRangeMax >= max) colorTempRangeMax = max;
		if (+colorTempRangeMin <= min) colorTempRangeMin = min;
		if (+colorTempRangeMin >= max) colorTempRangeMin = +colorTempRangeMax - 1;
		//prevent sending lower max value compared to to min value. Also if min value increases max value will be plus 1. And limitations between min and max
	};

	$: colorTempRangeMinData = getErrorOrInfoMessageForMinAndMax(colorTempRangeMin, min, max);
	$: colorTempRangeMaxData = getErrorOrInfoMessageForMinAndMax(colorTempRangeMax, min, max);
</script>

<Alert
	class="mb-s"
	heading={$format('alert_heading_ct_range', {
		default: `Remaps output color temperature min/max range to the specified values here.`,
	})}
/>
<Text styleType="body-small" class="mb-xs">
	{$format('min')}
</Text>
<Input
	class="mb-s"
	size="m"
	bind:value={colorTempRangeMin}
	type={'number'}
	{min}
	max={max - 1}
	label={$format('min')}
	error={colorTempRangeMinData.error}
	info={colorTempRangeMinData.info}
	step={1}
	on:change={validate}
	on:enter={setcolorTempRange}
	id="input-uk44iqb"
	aria-labelledby="input-uk44iqb"
/>
<Text styleType="body-small" class="mb-xs">
	{$format('max')}
</Text>
<Input
	size="m"
	bind:value={colorTempRangeMax}
	type={'number'}
	{min}
	{max}
	label={$format('max')}
	error={colorTempRangeMaxData.error}
	info={colorTempRangeMaxData.info}
	step={1}
	on:change={validate}
	on:enter={setcolorTempRange}
	id="input-duj8rn3"
	aria-labelledby="input-duj8rn3"
/>
<Button class="mt-s" {loading} size="m" width="full" on:click={setcolorTempRange}>
	{$format('button_save')}
</Button>
